import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import NavigationBar from '@/components/ui/NavigationBar';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import UGCLayout from '@/components/layouts/UGCLayout';
import { ugcMarketplaceService } from '@/services/ugcMarketplaceService';
import { generateInvoicePDF } from '@/utils/invoiceGenerator';

const navItems = [
  { path: '/app/ugc-dashboard', label: 'Dashboard', icon: 'BarChart3' },
  { path: '/app/ugc-marketplace', label: 'Marketplace', icon: 'ShoppingBag' },
  { path: '/app/ugc-invoices', label: 'Invoices', icon: 'DollarSign' },
];

const CreatorInvoices = () => {
  const { profile } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, [profile?.id]);

  const loadInvoices = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const allInvoices = await ugcMarketplaceService.listMyInvoices(profile.id);
      // Filter to creator's earned invoices
      setInvoices(allInvoices.filter(i => i.creator_user_id === profile.id));
    } catch (err) {
      console.error('load invoices failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (invoice) => {
    try {
      generateInvoicePDF(invoice, 'creator');
    } catch (err) {
      console.error('PDF generation failed', err);
    }
  };

  const statusBadge = (status) => {
    const colors = {
      draft: 'bg-slate-500/20 text-slate-200',
      sent: 'bg-blue-500/20 text-blue-200',
      paid: 'bg-emerald-500/20 text-emerald-200',
      overdue: 'bg-red-500/20 text-red-200'
    };
    return colors[status] || colors.draft;
  };

  return (
    <UGCLayout>
      <NavigationBar items={navItems} hideLocks />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-100 text-xs font-semibold border border-amber-500/30 mb-3">
            <Icon name="DollarSign" size={14} /> My Invoices
          </div>
          <h1 className="text-3xl font-bold text-white">Invoices earned</h1>
          <p className="text-amber-100/80">Track and download invoices from accepted bids.</p>
        </div>

        <div className="mt-8">
          {invoices.length === 0 ? (
            <div className="text-center py-12 bg-slate-800/30 border border-slate-700 rounded-lg">
              <Icon name="FileText" size={48} className="mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">No invoices yet. Accept a bid to get started!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map(invoice => (
                <div key={invoice.id} className="bg-slate-800/30 border border-slate-700 rounded-lg p-4 flex justify-between items-center">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="font-semibold text-white">Invoice #{invoice.id?.slice(0, 8).toUpperCase()}</p>
                      <span className={`text-xs px-2 py-1 rounded ${statusBadge(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400">
                      Amount: <span className="text-amber-300 font-semibold">${invoice.amount?.toFixed(2)}</span>
                      {invoice.due_date && ` • Due: ${new Date(invoice.due_date).toLocaleDateString()}`}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleDownload(invoice)}
                    className="flex items-center gap-2"
                  >
                    <Icon name="Download" size={16} /> Download
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </UGCLayout>
  );
};

export default CreatorInvoices;
